import axios from 'axios';
import { DOCS_URL, MEET_URL, enDocs, enMeet, enAliasCorreo, volverAlCorreo } from './dominios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// En docs.orvian.mx (y en el dominio anterior del correo) la API va por su propio dominio (nginx la
// publica igual): sin CORS ni preflight. Lo mismo en meet.orvian.mx (Orvian Meet).
export const API_BASE = enDocs() ? DOCS_URL : enMeet() ? MEET_URL : enAliasCorreo() ? window.location.origin : BACKEND_URL;
export const API = `${API_BASE}/api`;

export const api = axios.create({ baseURL: API });

export function getTokens() {
  return {
    access: localStorage.getItem('zuno_access'),
    refresh: localStorage.getItem('zuno_refresh'),
  };
}
export function setTokens(access, refresh) {
  if (access) localStorage.setItem('zuno_access', access);
  if (refresh) localStorage.setItem('zuno_refresh', refresh);
}
/** Cierra la sesión EN EL SERVIDOR antes de borrar el navegador (S3, auditoría 2026-10-05).
 *  Antes «Cerrar sesión» solo borraba localStorage y, con sesiones que ya no caducan, cualquier copia
 *  del token (otra pestaña, docs.orvian.mx) seguía valiendo. Si no hay red, se cierra igual aquí. */
export async function cerrarSesionEnServidor() {
  const { refresh } = getTokens();
  if (!refresh || estaDelegado()) return;
  try {
    await api.post('/auth/logout', { refresh_token: refresh }, { timeout: 5000, skipAuthRedirect: true });
  } catch (_) { /* sin red o ya cerrada: el navegador se limpia igual */ }
}

export function clearTokens() {
  localStorage.removeItem('zuno_access');
  localStorage.removeItem('zuno_refresh');
  localStorage.removeItem('zuno_user');
  localStorage.removeItem(DELEGACION);
}

/* ---------------- Acceso delegado (un admin dentro de un buzón ajeno) ----------------
   La sesión del admin se guarda APARTE y los tokens activos pasan a ser los del
   buzón visitado, así el resto de la aplicación no se entera de nada: pide /mail
   como siempre y el backend le responde con el correo de esa persona.
   El token delegado NO tiene refresh a propósito (caduca solo, ver
   IMPERSONATION_TTL_MINUTES): por eso al salir hay que reponer los del admin. */
const DELEGACION = 'zuno_deleg';

export function infoDelegacion() {
  try { return JSON.parse(localStorage.getItem(DELEGACION) || 'null'); } catch { return null; }
}
export function estaDelegado() { return !!localStorage.getItem(DELEGACION); }

export function entrarDelegado({ access, user, quien }) {
  const { access: adminAccess, refresh: adminRefresh } = getTokens();
  localStorage.setItem(DELEGACION, JSON.stringify({
    admin_access: adminAccess || null,
    admin_refresh: adminRefresh || null,
    admin_user: localStorage.getItem('zuno_user') || null,
    buzon: { id: user?.id, email: user?.email, name: user?.name },
    quien: quien || null,
    desde: Date.now(),
  }));
  localStorage.setItem('zuno_access', access);
  localStorage.removeItem('zuno_refresh');
  if (user) localStorage.setItem('zuno_user', JSON.stringify(user));
}

/** Devuelve la sesión del admin restaurada, o null si no había delegación. */
export function salirDelegado() {
  const d = infoDelegacion();
  if (!d) return null;
  localStorage.removeItem(DELEGACION);
  if (d.admin_access) localStorage.setItem('zuno_access', d.admin_access);
  else localStorage.removeItem('zuno_access');
  if (d.admin_refresh) localStorage.setItem('zuno_refresh', d.admin_refresh);
  else localStorage.removeItem('zuno_refresh');
  if (d.admin_user) localStorage.setItem('zuno_user', d.admin_user);
  else localStorage.removeItem('zuno_user');
  return d;
}

api.interceptors.request.use((config) => {
  const { access } = getTokens();
  if (access) config.headers.Authorization = `Bearer ${access}`;
  return config;
});

let refreshing = null;

function irAlLogin() {
  if (window.location.pathname.includes('/login')) return;
  // En docs no se entra: la misma ruta en el correo pide entrar, guarda el documento y luego
  // devuelve a docs. Mandar a /login de docs perdía el documento por el camino.
  if (enDocs()) { volverAlCorreo(); return; }
  // Sesión caducada o cerrada desde otro equipo (A4): antes era una recarga dura a /login sin
  // explicación y se perdía la pantalla. Se apunta a dónde volver (Login lo lee en `entrar`)
  // y se dice por qué.
  try {
    const aqui = window.location.pathname + window.location.search;
    if (!/^\/(ayuda-acceso)?$/.test(window.location.pathname)) sessionStorage.setItem('zuno_next', aqui);
  } catch (_) {}
  window.location.href = '/login?motivo=sesion';
}

function isPublicDrivePath() {
  try {
    // /app/: la vista del editor dentro de la app del teléfono. Allí la sesión la lleva la app, que
    // renueva el token; mandar a /login dentro de una vista web sin salida dejaría a la persona atrapada.
    // /f/: responder un formulario de Orvian Forms (público; si pide sesión lo dice la propia página)
    // Orvian Meet: se puede usar sin sesión (invitado); la propia página ofrece «Iniciar sesión»
    if (enMeet()) return true;
    return /^\/(open|app|f)\//.test(window.location.pathname || '');
  } catch {
    return false;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const skipLogin =
      original.skipAuthRedirect ||
      isPublicDrivePath() ||
      String(original.url || '').includes('/drive/open/');
    if (error.response && error.response.status === 401 && !original._retry) {
      original._retry = true;
      // Delegación caducada o revocada: NO se cierra la sesión del admin, se le
      // devuelve la suya y se le avisa. Cerrarla sería castigarle por mirar.
      if (estaDelegado()) {
        // En docs.orvian.mx no se toca la sesión: lo que se borra aquí se refleja en el correo y sacaría
        // al admin de SU sesión (D1). Se vuelve al correo, que gestiona el fin de la delegación.
        if (enDocs()) { volverAlCorreo(); return Promise.reject(error); }
        salirDelegado();
        try { sessionStorage.setItem('zuno_deleg_fin', '1'); } catch (_) {}
        window.location.href = '/admin';
        return Promise.reject(error);
      }
      const { refresh } = getTokens();
      if (!refresh) {
        if (!skipLogin) {
          clearTokens();
          irAlLogin();
        } else if (enMeet()) {
          clearTokens();   // Meet: la copia caducada de la sesión estorba; se sigue como invitado
        }
        return Promise.reject(error);
      }
      try {
        if (!refreshing) {
          refreshing = axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
        }
        const res = await refreshing;
        refreshing = null;
        // El servidor devuelve también un refresh de larga duración (la sesión no caduca, 2026-10-05).
        setTokens(res.data.access_token, res.data.refresh_token || null);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${res.data.access_token}`;
        return api(original);
      } catch (e) {
        refreshing = null;
        /* Solo un «no» del servidor cierra la sesión (401/403: revocada, cerrada desde otro equipo o
           token inválido). Antes CUALQUIER fallo de la renovación —un corte de red, el portátil al
           despertar, un 502 mientras se reinicia el servidor— borraba la sesión y mandaba al acceso
           en mitad de un documento. Ahora esos fallos solo hacen fallar esta petición; la siguiente
           vuelve a intentar renovar. */
        const status = e?.response?.status;
        if (status !== 401 && status !== 403) {
          return Promise.reject(error);
        }
        if (!skipLogin) {
          clearTokens();
          irAlLogin();
        } else if (enMeet()) {
          // Meet guarda solo una COPIA de la sesión del correo: si ya no se renueva, se descarta para que las
          // rutas de invitado no sigan mandando un token muerto (el backend responde 401 a un token caducado).
          clearTokens();
        }
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

/** Public drive open without forcing login redirect */
export function publicApiGet(path) {
  return api.get(path, { skipAuthRedirect: true });
}
export function publicApiPut(path, data) {
  return api.put(path, data, { skipAuthRedirect: true });
}
